import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { RecordData } from 'src/app/types';
import { RecordingEvent, SeekEvent } from '../../measured-data-details.types';
import { MeasurementEvent, ParsedMeasurement } from '../../models/parsed-measurement.model';
import { PlayerSnapshot } from '../../services/player-controller.service';
import { EChartsOption } from 'echarts';
import { NeckOrientation } from '../../services/neck-angle.service';
import type { ECharts } from 'echarts';

import { DetailsHeaderComponent } from '../shared/details-header/details-header.component';
import { ObservationChipsComponent } from '../shared/observation-chips/observation-chips.component';
import { EventTableComponent } from '../shared/event-table/event-table.component';
import { DataFooterComponent } from '../shared/data-footer/data-footer.component';
import { DisclaimerComponent } from '../shared/disclaimer/disclaimer.component';
import { MeasuredDataWaveCardComponent } from '../wave-card/measured-data-wave-card.component';
import { MeasuredDataVideoCardComponent } from '../video-card/measured-data-video-card.component';
import { MeasuredDataNeckAngleCardComponent } from '../neck-angle-card/measured-data-neck-angle-card.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-protocol-vs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    DetailsHeaderComponent,
    ObservationChipsComponent,
    EventTableComponent,
    DataFooterComponent,
    DisclaimerComponent,
    MeasuredDataWaveCardComponent,
    MeasuredDataVideoCardComponent,
    MeasuredDataNeckAngleCardComponent,
  ],
  templateUrl: './protocol-vs.component.html',
  styleUrl: './protocol-vs.component.scss',
})
export class ProtocolVsComponent implements OnDestroy {
  // ── Data inputs ──
  readonly recordData = input.required<RecordData>();
  readonly mediaRecordData = input<RecordData | null>(null);
  readonly parsedMeasurement = input<ParsedMeasurement | null>(null);
  readonly playerSnapshot = input<PlayerSnapshot | null>(null);
  /** マスター(WAV)由来の再生終了状態。WAV-master 時の動画カードの終了/replay 判定に使う。 */
  readonly playbackEnded = input<boolean>(false);
  /** 表示用ミュート上書き。VS(WAV-master) では WAV の実ミュート状態を渡す（非VSは null）。 */
  readonly audioMutedOverride = input<boolean | null>(null);

  // ── Wave card inputs ──
  readonly recordtype = input<number>(4);
  readonly recordLoadingSuccess = input<boolean>(true);
  readonly waveLoadingPercent = input<number | null>(null);
  readonly waveProcessing = input<boolean>(false);
  readonly waveDownloading = input<boolean>(false);
  readonly waveDownloadPercent = input<number | null>(null);
  readonly waveErrorMessage = input<string | null>(null);
  readonly selectedEvent = input<RecordingEvent | null>(null);
  readonly currentStartOffset = input<number>(0);
  readonly playing = input<boolean>(false);
  readonly timelineOffsetReference = input<number>(0);
  readonly zoomValue = input<number>(0);
  readonly showWaveCard = input<boolean>(true);
  readonly avSyncFrontTrimRatio = input<number>(0);
  readonly avSyncEndTrimRatio = input<number>(0);

  // ── Neck angle inputs ──
  readonly chartOption = input<EChartsOption>({});
  readonly currentChartOption = input<EChartsOption>({});
  readonly currentOrientation = input<NeckOrientation | null>(null);

  // ── Wave card outputs ──
  @Output() readonly offsetToggle = new EventEmitter<boolean>();
  @Output() readonly seek = new EventEmitter<number>();
  @Output() readonly playPauseClicked = new EventEmitter<void>();
  @Output() readonly zoomMinusClicked = new EventEmitter<void>();
  @Output() readonly zoomResetClicked = new EventEmitter<void>();
  @Output() readonly zoomPlusClicked = new EventEmitter<void>();

  // ── Video card outputs ──
  @Output() readonly videoPlayToggle = new EventEmitter<void>();
  @Output() readonly videoSeekTo = new EventEmitter<number>();
  @Output() readonly videoRateChange = new EventEmitter<number>();
  @Output() readonly videoMuteToggle = new EventEmitter<void>();
  @Output() readonly videoHostReady = new EventEmitter<HTMLElement>();

  // ── Event table outputs ──
  @Output() readonly eventSeek = new EventEmitter<SeekEvent>();

  // ── Neck angle outputs ──
  @Output() readonly chartInit = new EventEmitter<ECharts>();
  @Output() readonly currentChartInit = new EventEmitter<ECharts>();

  @Output() readonly waveHostReady = new EventEmitter<MeasuredDataWaveCardComponent>();
  private destroyed = false;
  private latestWaveCardRef?: MeasuredDataWaveCardComponent;

  @ViewChild(MeasuredDataWaveCardComponent)
  set waveCardRef(comp: MeasuredDataWaveCardComponent | undefined) {
    if (!comp || this.destroyed) return;
    this.latestWaveCardRef = comp;
    queueMicrotask(() => {
      if (this.destroyed || this.latestWaveCardRef !== comp) {
        return;
      }
      this.waveHostReady.emit(comp);
    });
  }

  // ── Computed ──
  protected readonly patientName = computed(() => {
    const d = this.recordData()?.data;
    return d?.userName ?? '';
  });

  protected readonly measureDate = computed(() => {
    const d = this.recordData()?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? '';
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === '+' && offsetMatch[2] === '00' && offsetMatch[3] === '00');

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, '').trim();
      if (!dateStr) return '';
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, '0')}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, '').trim();
    if (!dateStr) return '';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  });

  protected readonly measurer = computed(() => {
    const rd = this.recordData();
    const d = rd?.data;
    if (d?.recorderName) {
      return $localize`:@@details.protocolVs.measurerLabel:計測者: ${d.recorderName}`;
    }
    const m = rd?.meta?.measurer;
    if (m?.lastName || m?.firstName) {
      return $localize`:@@details.protocolVs.measurerLabel:計測者: ${(m.lastName ?? '') + (m.firstName ?? '')}`;
    }
    return '';
  });

  protected readonly events = computed<MeasurementEvent[]>(() =>
    this.parsedMeasurement()?.events ?? [],
  );

  protected readonly swallowCount = computed(() =>
    this.parsedMeasurement()?.vsKpi?.swallowCount ?? 0,
  );

  protected readonly coughCount = computed(() =>
    this.parsedMeasurement()?.vsKpi?.coughCount ?? 0,
  );

  protected readonly durationSec = computed(() =>
    this.parsedMeasurement()?.vsKpi?.measurementDurationSec ?? 0,
  );

  protected readonly dataId = computed(() =>
    this.recordData()?.id ?? '',
  );

  protected readonly downloadUrl = computed(() => {
    const d = this.recordData()?.data;
    return d?.storage?.csvUrl ?? '';
  });

  protected readonly disclaimer = $localize`:@@details.protocolVs.disclaimer:ビデオ聴診は観察記録です。嚥下音・咳嗽の自動識別にはAI推定値を含みます。結果は専門医の判断と併せてご利用ください。`;

  // ── Zone-ready computed signals ──
  // 各カードがデータを取得し終わったかを判定し、未準備の間は zone-skeleton を表示する。
  // 段階的にスケルトンを解除することで「動いている感」を演出する。

  /** ヘッダー（患者名・日時）。recordData が到着していれば即表示可能 */
  protected readonly headerReady = computed(() => !!this.recordData());

  /** 動画カード。mediaRecordData（getMediaの結果）または fallback streamingURL を保持する recordData が必要 */
  protected readonly videoReady = computed(() => !!this.mediaRecordData());

  protected readonly canPlayWaveStandalone = computed(() => {
    const status = (this.mediaRecordData()?.data?.mediaStatus ?? '').toLowerCase();
    const terminalVideoError =
      status === 'deleted' ||
      status === 'failed' ||
      status === 'error' ||
      status === 'canceled' ||
      status === 'cancelled';
    return terminalVideoError || this.playerSnapshot()?.state === 'error';
  });

  /** 姿勢（neck-angle）グラフ。chartOption に series が積まれていれば描画可能 */
  protected readonly postureReady = computed(() => {
    const opt = this.chartOption() as { series?: unknown[] } | null;
    if (!opt) return false;
    const series = opt.series;
    return Array.isArray(series) && series.length > 0;
  });

  /** イベント表・KPIチップ。parsedMeasurement が到着していればOK */
  protected readonly measurementReady = computed(() => !!this.parsedMeasurement());

  ngOnDestroy(): void {
    this.destroyed = true;
    this.latestWaveCardRef = undefined;
  }

  protected onEventSeek(event: SeekEvent): void {
    this.eventSeek.emit(event);
  }
}
